import { Box, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import useIsIntegrated from "../../api/hooks/useIsIntegrated";
import { platformUsersKeys } from "../../api/queryKeys";
import LoadingIcon from "../../components/LoadingIcon";
import AppPage from "../../components/structure/AppPage";
import { IntegrationsMapping } from "../stack/integrations/IntegrationsMapping";
import SettingsSideMenu from "./SettingsSideMenu";
import { categoryName, pageName } from "./constants";
import { pagesByCategories } from "./pages";

export const PageContext = createContext(null);
export const SettingsContext = createContext(null);

export const ORGANIZATION_NOTIFICATIONS_TAB = "organization-notifications";
export const PERSONAL_NOTIFICATIONS_TAB = "personal-notifications";

export default function SettingsView() {
  const colors = useColors();
  const { getPlatformUser, trackEvent } = useAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL params or use defaults
  const categoryFromUrl = searchParams.get("category");
  const pageFromUrl = searchParams.get("page");

  const [currentCategory, setCurrentCategory] = useState(
    categoryFromUrl && pagesByCategories[categoryFromUrl]
      ? categoryFromUrl
      : categoryName.ACCOUNT
  );
  const [currentPage, setCurrentPage] = useState(
    pageFromUrl && pagesByCategories[currentCategory]?.pages[pageFromUrl]
      ? pageFromUrl
      : pageName.PROFILE
  );

  const currentPlatformUser = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  // Preloading for immediate use in internal components
  const { isIntegratedLoading: isSlackLoading } = useIsIntegrated(
    IntegrationsMapping.slack.codeName
  );
  const { isIntegratedLoading: isTeamsLoading } = useIsIntegrated(
    IntegrationsMapping.microsoft_teams.codeName
  );

  const pageIsLoading =
    currentPlatformUser.isLoading || isSlackLoading || isTeamsLoading;

  // Update URL when category or page changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("category", currentCategory);
    params.set("page", currentPage);
    setSearchParams(params, { replace: true });
  }, [currentCategory, currentPage, setSearchParams]);

  // Track page changes
  useEffect(() => {
    const categoryTitle =
      pagesByCategories[currentCategory]?.menuTitle || currentCategory;
    const pageTitle =
      pagesByCategories[currentCategory]?.pages[currentPage]?.menuTitle ||
      currentPage;

    trackEvent(
      "User viewed settings page: " + categoryTitle + " > " + pageTitle
    );
  }, [currentCategory, currentPage, trackEvent]);

  const PageComponent =
    pagesByCategories[currentCategory].pages[currentPage].component;

  // NOTE: The "/settings?tab=" query parameter is primarily used by backend features
  // (e.g., Microsoft Teams welcome message and email scan notifications). Modify with caution.

  const queryTab = searchParams.get("tab");

  let newCategoryFromQueryParam = currentCategory;

  if (
    currentPlatformUser.isSuccess &&
    [ORGANIZATION_NOTIFICATIONS_TAB, PERSONAL_NOTIFICATIONS_TAB].includes(
      queryTab
    )
  ) {
    if (
      queryTab === ORGANIZATION_NOTIFICATIONS_TAB &&
      currentPlatformUser.data.is_admin
    ) {
      newCategoryFromQueryParam = categoryName.ORGANIZATION_NOTIFICATIONS;
    } else {
      newCategoryFromQueryParam = categoryName.PERSONAL;
    }
  }

  if (newCategoryFromQueryParam !== currentCategory) {
    setCurrentCategory(newCategoryFromQueryParam);
    setCurrentPage(pageName.GENERAL);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("tab");
      return newParams;
    });
  }

  return (
    <AppPage title="Settings">
      {pageIsLoading ? (
        <LoadingIcon />
      ) : currentPlatformUser.data && currentPlatformUser.isSuccess ? (
        <Box display="flex" flexDirection="row">
          <Box>
            <SettingsSideMenu
              currentCategory={currentCategory}
              setCurrentCategory={setCurrentCategory}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              isAdmin={currentPlatformUser.data.is_admin}
            />
          </Box>
          <Box>
            <Divider
              orientation="vertical"
              sx={{
                marginRight: "6px",
                marginLeft: "8px",
                color: colors.legend_separator,
              }}
            />
          </Box>
          <Box width="100%">
            <PageContext.Provider value={[currentCategory, currentPage]}>
              <PageComponent />
            </PageContext.Provider>
          </Box>
        </Box>
      ) : (
        <Typography>This page is currently unavailable</Typography>
      )}
    </AppPage>
  );
}
